import { toEnrichmentOptions } from '@/shared/enrichment/enrichment-options';

/**
 * Pure enrichment-bucket segmentation. Given already-fetched profiles it
 * segments them into new / stale / fresh buckets; the deterministic core,
 * with no fetching/abort/loading concerns.
 *
 * The stale rule (`STALE_DAYS = 30`) stays owned by the shared
 * `toEnrichmentOptions`, which is deliberately reused (not duplicated) so the
 * business rule can't drift between Add and the rest of the app.
 */

/** Minimal profile shape needed for segmentation (subset of `ProfileResponse`). */
export interface EnrichmentProfileInput {
  id: string;
  full_name: string;
  job_title: string | null;
  linkedin_id: string | null;
  data_updated_at: string | null;
}

/**
 * Segmented enrichment counts + id buckets. Mirrors the hook's `EnrichmentStats`
 * minus `loading`, which is a slice/saga concern, not a pure output.
 */
export interface EnrichmentStats {
  total: number;
  newCount: number;
  staleCount: number;
  freshCount: number;
  newIds: string[];
  staleIds: string[];
  allIds: string[];
  /** IDs that came from the filter-based fetch only (excludes imported). */
  filterOnlyIds: string[];
  /** Duplicates excluded; only meaningful when `excludeDuplicates` is true. */
  duplicateCount: number;
}

export const EMPTY_ENRICHMENT_STATS: EnrichmentStats = {
  total: 0,
  newCount: 0,
  staleCount: 0,
  freshCount: 0,
  newIds: [],
  staleIds: [],
  allIds: [],
  filterOnlyIds: [],
  duplicateCount: 0,
};

/**
 * @param filterProfiles   profiles matching the current server filters
 * @param rawTotal         total before duplicate exclusion (for the diff)
 * @param importedProfiles profiles pulled in from an import step
 * @param excludeDuplicates whether the filter fetch excluded duplicates
 */
export function buildEnrichmentStats(
  filterProfiles: EnrichmentProfileInput[],
  rawTotal: number,
  importedProfiles: EnrichmentProfileInput[],
  excludeDuplicates: boolean,
): EnrichmentStats {
  // Merge filter + imported, deduplicating by id and preserving order.
  const seenIds = new Set<string>();
  const allProfiles: EnrichmentProfileInput[] = [];
  for (const p of filterProfiles) {
    if (!seenIds.has(p.id)) {
      seenIds.add(p.id);
      allProfiles.push(p);
    }
  }
  for (const p of importedProfiles) {
    if (!seenIds.has(p.id)) {
      seenIds.add(p.id);
      allProfiles.push(p);
    }
  }

  const opts = toEnrichmentOptions(allProfiles);
  const newOpts = opts.filter((p) => p.isNew);
  const staleOpts = opts.filter((p) => !p.isNew && p.isStale);

  return {
    total: opts.length,
    newCount: newOpts.length,
    staleCount: staleOpts.length,
    freshCount: opts.filter((p) => !p.isNew && !p.isStale).length,
    newIds: newOpts.map((p) => p.id),
    staleIds: staleOpts.map((p) => p.id),
    allIds: opts.map((p) => p.id),
    filterOnlyIds: filterProfiles.map((p) => p.id),
    duplicateCount: excludeDuplicates ? Math.max(0, rawTotal - filterProfiles.length) : 0,
  };
}
